import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Close";
import { axiosInstance } from "../../utility/apiInstance";
import {
  Autocomplete,
  TextField,
  Chip,
  Checkbox,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import {
  GridRowsProp,
  GridRowModesModel,
  GridRowModes,
  DataGrid,
  GridColDef,
  GridActionsCellItem,
  GridEventListener,
  GridRowId,
  GridRowModel,
  GridRowEditStopReasons,
  GridSlots,
} from "@mui/x-data-grid";
import toast from "react-hot-toast";
import { preventAddingDuplocateRow } from "../../utility/constant";
import clsx from "clsx";
import SearchInput from "../../common/SearchInput";
import { CustomLoadingOverlay, CustomNoRowsOverlay } from "../../common/Datagridoverlays";
import { FOOTER_HEIGHT, HEADER_HEIGHT, MAX_GRID_HEIGHT, MIN_GRID_HEIGHT, ROW_ESTIMATE } from "../../components/Contants/constantValue";

function randomId() {
  return Math.random().toString(36).substr(2, 9);
}

const id = randomId();

// EditToolbar now returns null — Add Record button moved outside DataGrid
function EditToolbar() {
  return null;
}

// ── Fixed, comfortable column widths — never shrunk for smaller screens.
// The grid scrolls horizontally instead of squeezing content unreadable.
const COL_WIDTHS = {
  s_no: 70,
  templatename: 160,
  waContacts: 260,
  waMilestones: 300,
  emailContacts: 260,
  emailMilestones: 300,
  whatsappGroups: 220,
  whatsappGroupMilestones: 240,
  actions: 150,
};

const GRID_MIN_WIDTH = Object.values(COL_WIDTHS).reduce((a, b) => a + b, 0);

export default function FullFeaturedCrudGrid() {

  const [rows, setRows] = React.useState([]);
  const [rowdata, setRowdata] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [contactdata, setContactdata] = React.useState({
    whatsApp: [],
    email: [],
  });
  const [currentPage, setCurrentPage] = React.useState(0);
  const [milestonedata, setMilestonedata] = React.useState([]);
  const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>({});
  const [whatsappGroup, setWhatsappGroup] = React.useState([]);
  const [isAddDisabled, setIsAddDisabled] = React.useState(false);

  // ── Search state ──────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");

  // Debounce search input
  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  // Re-fetch on debounced search change
  React.useEffect(() => {
    getTemplatedata(debouncedSearch);
  }, [debouncedSearch]);
  // ─────────────────────────────────────────────────────────────

  const getContactList = async () => {
    try {
      const [emailResp, whatsappResp] = await Promise.all([
        axiosInstance.get("/api/v1/contact?channel=email"),
        axiosInstance.get("/api/v1/contact?channel=whatsapp"),
      ]);
      setContactdata((prev: any) => ({
        ...prev,
        whatsApp: whatsappResp?.data?.data,
        email: emailResp?.data?.data,
      }));
    } catch (err: any) { }
  };

  const getTemplatedata = async (search: string = "") => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(
        `/api/v1/ocean/template?search=${encodeURIComponent(search)}`
      );
      setRows(response.data.data);
    } catch (error) {
      setRows([])
    } finally {
      setLoading(false);
    }
  };

  const getmilestone = async () => {
    try {
      const response = await axiosInstance.get("/api/v1/ocean/ocean-milestone");
      setMilestonedata(response?.data?.data);
    } catch (error) { }
  };

  const getWhatsappGroup = async () => {
    try {
      const response = await axiosInstance.get("/api/v1/whatsapp-group");
      setWhatsappGroup(response.data.data);
    } catch (error) { }
  };

  React.useEffect(() => {
    getTemplatedata(debouncedSearch);
    getContactList();
    getmilestone();
    getWhatsappGroup();
  }, []);

  // ── Add Record handler (moved out of toolbar) ─────────────────
  const handleAddClick = () => {
    const newId = Math.random();
    setRows((oldRows: any) => {
      if (!preventAddingDuplocateRow(oldRows)) {
        toast.error("You can only submit one record at a time!");
        return oldRows;
      }
      setIsAddDisabled(true);
      setRowModesModel((oldModel) => ({
        ...oldModel,
        [newId]: { mode: GridRowModes.Edit, fieldToFocus: "templatename" },
      }));
      return [
        {
          id: newId,
          templatename: "",
          waContacts: [],
          waMilestones: [],
          emailContacts: [],
          emailMilestones: [],
          whatsappGroups: [],
          whatsappGroupMilestones: [],
          isActive: true,
          isNew: true,
        },
        ...oldRows,
      ];
    });
  };
  // ─────────────────────────────────────────────────────────────

  const handleRowEditStop: GridEventListener<"rowEditStop"> = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = (id: GridRowId, row: any) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
    setIsAddDisabled(true);
  };

  const handleSaveClick = (id: GridRowId) => async () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
    setIsAddDisabled(false);
  };

  const handleDeleteClick = (id: GridRowId, row: any) => async () => {
    const url = `/api/v1/ocean/template/${id}`;
    try {
      const response = await axiosInstance.delete(url);
      getTemplatedata(debouncedSearch);
      if (response.status === 200) {
        toast.success("Subscription Template deleted successfully.");
      }
    } catch (error: any) {
      toast.error("An error occurred while deleting Subscription Template.");
    }
  };

  const handleCancelClick = (id: GridRowId) => async () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });
    const editedRow: any = rows.find((row: any) => row.id === id);
    if (editedRow?.isNew) {
      setRows(rows.filter((row: any) => row.id !== id));
    }
    setIsAddDisabled(false);
  };

  const processRowUpdate = async (newRow: GridRowModel) => {
    const updatedRow: any = { ...newRow, isNew: false };
    setRowdata(updatedRow);

    if (!updatedRow.templatename) {
      toast.error("Template Name is required");
      setIsAddDisabled(true);
      return;
    } else if (
      !updatedRow.waContacts ||
      !updatedRow.waMilestones ||
      !updatedRow.emailContacts ||
      !updatedRow.emailMilestones ||
      !updatedRow.whatsappGroups ||
      !updatedRow.whatsappGroupMilestones
    ) {
      toast.error("Contacts and Milestones are required");
      setIsAddDisabled(true);
      return;
    }

    if (updatedRow.id < 1) {
      updatedRow.id = id;
      const formData = {
        templateName: updatedRow.templatename,
        waContacts: updatedRow.waContacts.map((contact: any) => contact._id),
        waMilestones: updatedRow.waMilestones.map((milestone: any) => milestone?._id),
        emailContacts: updatedRow.emailContacts.map((contact: any) => contact?._id),
        emailMilestones: updatedRow.emailMilestones.map((milestone: any) => milestone?._id),
        whatsappGroups: updatedRow.whatsappGroups.map((contact: any) => contact?._id),
        whatsappGroupMilestones: updatedRow.whatsappGroupMilestones.map(
          (milestone: any) => milestone?._id
        ),
      };

      try {
        let response = await axiosInstance.post("/api/v1/ocean/template", formData);
        if (response.data.statusCode === 201) {
          toast.success(response.data.message);
          getTemplatedata(debouncedSearch);
          setRows((prevRows: any) =>
            prevRows.map((row: any) => (row.id === newRow.id ? updatedRow : row))
          );
          return updatedRow;
        }
      } catch (err: any) {
        const errors = err?.response?.data?.error;
        if (Array.isArray(errors)) {
          errors.forEach((errorObj) => {
            const { field, message } = errorObj;
            toast.error(`${field}: ${message}`);
          });
        } else if (typeof errors === "object") {
          Object.keys(errors).forEach((field) => {
            toast.error(`${field}: ${errors[field]}`);
          });
        } else if (typeof errors === "string") {
          toast.error(errors);
        }
      }
    } else {
      if (!updatedRow.templatename) {
        toast.error("Template Name is required");
        setIsAddDisabled(true);
        return;
      }

      if (
        updatedRow?.templateName &&
        !updatedRow?.waContacts?.length &&
        !updatedRow?.emailContacts?.length
      ) {
        toast.error("Either WhatsApp contacts or Email Contacts is required");
        setIsAddDisabled(true);
        return;
      }

      if (
        updatedRow?.templateName &&
        updatedRow?.waContacts?.length > 0 &&
        !updatedRow?.waMilestones?.length
      ) {
        toast.error("Atleast one mile-stone is required for WhatsApp contact");
        setIsAddDisabled(true);
        return;
      }

      if (
        updatedRow?.templateName &&
        updatedRow?.emailContacts?.length > 0 &&
        !updatedRow?.emailMilestones?.length
      ) {
        toast.error("Atleast one Mile-stone is required for email contact");
        setIsAddDisabled(true);
        return;
      } else {
        const formData = {
          templateName: updatedRow?.templatename,
          waContacts: updatedRow?.waContacts?.map((contact: any) => contact?._id),
          waMilestones: updatedRow?.waMilestones?.map((milestone: any) => milestone?._id),
          emailContacts: updatedRow?.emailContacts?.map((contact: any) => contact._id),
          emailMilestones: updatedRow?.emailMilestones?.map((milestone: any) => milestone?._id),
          whatsappGroups: updatedRow.whatsappGroups.map((contact: any) => contact?._id),
          whatsappGroupMilestones: updatedRow.whatsappGroupMilestones.map(
            (milestone: any) => milestone?._id
          ),
        };

        const url = `/api/v1/ocean/template/${updatedRow.id}`;

        try {
          const response = await axiosInstance.patch(url, formData);
          getTemplatedata(debouncedSearch);

          const errorData = response.data?.error;
          if (typeof errorData === "object" && !Array.isArray(errorData)) {
            Object.keys(errorData).forEach((field) => {
              const message = errorData[field];
              toast.error(`${field}: ${message}`);
            });
          } else {
            setRows((prevRows: any) =>
              prevRows.map((row: any) => (row.id === newRow.id ? updatedRow : row))
            );
            toast.success(response.data.message);
            return updatedRow;
          }
        } catch (err: any) {
          if (Array.isArray(err.response?.data?.error)) {
            err.response.data.error.forEach((error: any) => {
              toast.error(`${error.field}: ${error.message}`);
            });
          } else {
            toast.error(
              err.response?.data?.error ||
              "An error occurred while updating Subscription Template."
            );
          }
        }
      }
    }
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const handlePaginationChange = (paginationModel: any) => {
    setCurrentPage(paginationModel.page);
  };

  const gridHeight = Math.min(
    MAX_GRID_HEIGHT,
    Math.max(
      MIN_GRID_HEIGHT,
      HEADER_HEIGHT + FOOTER_HEIGHT + Math.max(rows.length, 1) * ROW_ESTIMATE
    )
  );

  const columns: GridColDef[] = [
    {
      field: "s_no",
      headerName: "S.No.",
      width: COL_WIDTHS.s_no,
      sortable: true,
      filterable: false,
      editable: false,
      disableColumnMenu: true,
    },

    {
      field: "templatename",
      headerName: "Template Name",
      width: COL_WIDTHS.templatename,
      sortable: true,
      filterable: true,
      editable: true,

      renderEditCell: (params) => (
        <TextField
          value={params.value || ""}
          onChange={(e) =>
            params.api.setEditCellValue({
              id: params.id,
              field: params.field,
              value: e.target.value,
            })
          }
          variant="outlined"
          size="small"
          fullWidth
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "4px",
              fontSize: "14px",
              backgroundColor: "#fff",

              "& fieldset": {
                borderColor: "#ddd",
              },

              "&:hover fieldset": {
                borderColor: "#ffb310",
              },

              "&.Mui-focused fieldset": {
                borderColor: "#ffb310",
                borderWidth: "2px",
              },
            },

            "& .MuiInputBase-input": {
              padding: "8px 12px",
            },
          }}
        />
      ),
    },

    {
      field: "waContacts",
      headerName: "Whatsapp Recipient",
      width: COL_WIDTHS.waContacts,
      sortable: true,
      filterable: false,
      editable: true,
      renderCell: (params) => (
        <p>{params.row?.waContacts?.map((contact: any) => contact.name).join(", ")}</p>
      ),
      renderEditCell: (params) => {
        const { whatsApp } = contactdata;
        return (
          <Autocomplete
            multiple
            options={whatsApp || []}
            getOptionLabel={(option: any) =>
              `${option.salutation}. ${option.name} (${option.countryCode}${option.whatsApp}, ${option.organizationName})`
            }
            value={whatsApp.filter((item: any) =>
              params.row.waContacts
                .map((contact: any) => contact?.name)
                ?.includes(item.name)
            )}
            onChange={(event, newValue) => {
              params.api.setEditCellValue({ id: params.id, field: params.field, value: newValue });
            }}
            disableCloseOnSelect
            renderOption={(props, option: any, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {`${option.salutation}. ${option.name} (${option.whatappNumber
                  ? `${option.countryCode}${option.whatappNumber}`
                  : ""
                  }${option.organizationName ? `, ${option.organizationName}` : ""})`}
              </li>
            )}
            fullWidth
            renderTags={(value, getTagProps) =>
              value.map((option: any, index) => (
                <React.Fragment key={option.id || index}>
                  <Chip
                    variant="outlined"
                    label={`${option.salutation}. ${option.name} (${option.countryCode}${option.whatappNumber
                      }${option.organizationName ? `, ${option.organizationName}` : ""})`}
                    {...getTagProps({ index })}
                    sx={{ marginBottom: "4px" }}
                  />
                </React.Fragment>
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  style: { paddingTop: 0, paddingBottom: 0, paddingLeft: 0, maxHeight: 150, overflowY: "auto" },
                }}
              />
            )}
          />
        );
      },
    },
    {
      field: "waMilestones",
      headerName: "Whatsapp Milestone",
      sortable: true,
      filterable: false,
      editable: true,
      width: COL_WIDTHS.waMilestones,
      renderCell: (params) => (
        <p>{params.row?.waMilestones?.map((milestone: any) => milestone?.label + ",")}</p>
      ),
      renderEditCell: (params) => (
        <Autocomplete
          multiple
          options={milestonedata || []}
          getOptionLabel={(option: any) => option.milestone}
          value={milestonedata.filter((item: any) =>
            params.row.waMilestones
              ?.map((milestone: any) => milestone?.milestone)
              ?.includes(item.milestone)
          )}
          onChange={(event, newValue) => {
            params.api.setEditCellValue({ id: params.id, field: params.field, value: newValue });
          }}
          disableCloseOnSelect
          fullWidth
          renderOption={(props, option: any, { selected }) => (
            <li {...props}>
              <Checkbox
                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                checkedIcon={<CheckBoxIcon fontSize="small" />}
                style={{ marginRight: 8 }}
                checked={selected}
              />
              {option.label}
            </li>
          )}
          renderTags={(value, getTagProps) =>
            value.map((option: any, index) => (
              <React.Fragment key={option.id || index}>
                <Chip
                  variant="outlined"
                  label={option.label}
                  {...getTagProps({ index })}
                  sx={{ margin: "4px 2px" }}
                />
              </React.Fragment>
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                style: {
                  paddingTop: 0, paddingBottom: 0, paddingLeft: 0,
                  whiteSpace: "normal",
                  overflowY: "auto",
                  maxHeight: 200,
                },
              }}
            />
          )}
        />
      ),
    },
    {
      field: "emailContacts",
      headerName: "Email Recipient",
      sortable: true,
      filterable: false,
      editable: true,
      width: COL_WIDTHS.emailContacts,
      renderCell: (params) => (
        <p>{params.row?.emailContacts?.map((contact: any) => contact.name).join(", ")}</p>
      ),
      renderEditCell: (params) => {
        const { email } = contactdata;
        return (
          <Autocomplete
            multiple
            options={email || []}
            getOptionLabel={(option: any) =>
              `${option.salutation}. ${option.name} (${option.email}, ${option.organizationName})`
            }
            value={email.filter((item: any) =>
              params.row.emailContacts
                ?.map((contact: any) => contact?.name)
                ?.includes(item.name)
            )}
            onChange={(event, newValue) => {
              params.api.setEditCellValue({ id: params.id, field: params.field, value: newValue });
            }}
            disableCloseOnSelect
            renderOption={(props, option: any, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {`${option.salutation}. ${option.name} (${option.email}${option.organizationName ? `, ${option.organizationName}` : ""
                  })`}
              </li>
            )}
            fullWidth
            renderTags={(value, getTagProps) =>
              value.map((option: any, index) => (
                <React.Fragment key={option.id || index}>
                  <Chip
                    variant="outlined"
                    label={`${option.salutation}. ${option.name} (${option.email}, ${option.organizationName})`}
                    {...getTagProps({ index })}
                    sx={{ marginBottom: "4px" }}
                  />
                </React.Fragment>
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  style: { paddingTop: 0, paddingBottom: 0, paddingLeft: 0, maxHeight: 150, overflowY: "auto" },
                }}
              />
            )}
          />
        );
      },
    },
    {
      field: "emailMilestones",
      headerName: "Email Milestone",
      width: COL_WIDTHS.emailMilestones,
      sortable: true,
      filterable: false,
      editable: true,
      renderCell: (params) => (
        <p>{params.row?.emailMilestones?.map((milestone: any) => milestone?.label + ",")}</p>
      ),
      renderEditCell: (params) => (
        <Autocomplete
          multiple
          options={milestonedata || []}
          getOptionLabel={(option: any) => option.milestone}
          value={milestonedata.filter((item: any) =>
            params.row.emailMilestones
              ?.map((milestone: any) => milestone?.milestone)
              ?.includes(item.milestone)
          )}
          onChange={(event, newValue) => {
            params.api.setEditCellValue({ id: params.id, field: params.field, value: newValue });
          }}
          disableCloseOnSelect
          renderOption={(props, option: any, { selected }) => (
            <li style={{ fontSize: "13px" }} {...props}>
              <Checkbox
                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                checkedIcon={<CheckBoxIcon fontSize="small" />}
                style={{ marginRight: 8 }}
                checked={selected}
              />
              {option.label}
            </li>
          )}
          fullWidth
          renderTags={(value, getTagProps) =>
            value.map((option: any, index) => (
              <React.Fragment key={option.id || index}>
                <Chip
                  variant="outlined"
                  label={option.label}
                  {...getTagProps({ index })}
                  sx={{ margin: "4px 2px" }}
                />
              </React.Fragment>
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                style: {
                  paddingTop: 0, paddingBottom: 0, paddingLeft: 0,
                  whiteSpace: "normal",
                  overflowY: "auto",
                  maxHeight: 200,
                },
              }}
            />
          )}
        />
      ),
    },

    {
      field: "whatsappGroups",
      headerName: "Whatsapp Group",
      width: COL_WIDTHS.whatsappGroups,
      sortable: true,
      filterable: false,
      editable: true,
      renderCell: (params) => (
        <p>{params.row?.whatsappGroups?.map((contact: any) => contact.name).join(", ")}</p>
      ),
      renderEditCell: (params) => (
        <Autocomplete
          multiple
          options={whatsappGroup || []}
          getOptionLabel={(option: any) => `${option.name}`}
          value={whatsappGroup.filter((item: any) =>
            params.row.whatsappGroups
              .map((contact: any) => contact?.name)
              ?.includes(item.name)
          )}
          onChange={(event, newValue) => {
            params.api.setEditCellValue({ id: params.id, field: params.field, value: newValue });
          }}
          disableCloseOnSelect
          renderOption={(props, option: any, { selected }) => (
            <li {...props}>
              <Checkbox
                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                checkedIcon={<CheckBoxIcon fontSize="small" />}
                style={{ marginRight: 8 }}
                checked={selected}
              />
              {`${option.name}`}
            </li>
          )}
          fullWidth
          renderTags={(value, getTagProps) =>
            value.map((option: any, index) => (
              <React.Fragment key={option.id || index}>
                <Chip
                  variant="outlined"
                  label={`${option.name}`}
                  {...getTagProps({ index })}
                  sx={{ marginBottom: "4px" }}
                />
              </React.Fragment>
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                style: { paddingTop: 0, paddingBottom: 0, paddingLeft: 0, maxHeight: 150, overflowY: "auto" },
              }}
            />
          )}
        />
      ),
    },

    {
      field: "whatsappGroupMilestones",
      headerName: "Whatsapp Group Milestone",
      sortable: true,
      filterable: false,
      editable: true,
      width: COL_WIDTHS.whatsappGroupMilestones,
      flex: 0,
      renderCell: (params) => (
        <p>
          {params.row?.whatsappGroupMilestones?.map(
            (milestone: any) => milestone?.label + ","
          )}
        </p>
      ),
      renderEditCell: (params) => (
        <Autocomplete
          multiple
          options={milestonedata || []}
          getOptionLabel={(option: any) => option.milestone}
          value={milestonedata.filter((item: any) =>
            params.row.whatsappGroupMilestones
              ?.map((milestone: any) => milestone?.milestone)
              ?.includes(item.milestone)
          )}
          onChange={(event, newValue) => {
            params.api.setEditCellValue({ id: params.id, field: params.field, value: newValue });
          }}
          disableCloseOnSelect
          fullWidth
          renderOption={(props, option: any, { selected }) => (
            <li style={{ fontSize: "13px" }} {...props}>
              <Checkbox
                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                checkedIcon={<CheckBoxIcon fontSize="small" />}
                style={{ marginRight: 4 }}
                checked={selected}

              />
              {option.label}
            </li>
          )}
          renderTags={(value, getTagProps) =>
            value.map((option: any, index) => (
              <React.Fragment key={option.id || index}>
                <Chip
                  variant="outlined"
                  label={option.label}
                  {...getTagProps({ index })}
                  sx={{ margin: "4px 2px" }}
                />
              </React.Fragment>
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                style: {
                  paddingTop: 0, paddingBottom: 0, paddingLeft: 0,
                  whiteSpace: "normal",
                  overflowY: "auto",
                  maxHeight: 200,
                },
              }}
            />
          )}
        />
      ),
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: COL_WIDTHS.actions,
      cellClassName: "actions",
      getActions: ({ id, row }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
        if (isInEditMode) {
          return [
            <GridActionsCellItem
              key={`save-${id}`}
              icon={<SaveIcon />}
              label="Save"
              sx={{ color: "primary.main" }}
              onClick={handleSaveClick(id)}
              className="iconMilestone_save"
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              key={`cancel-${id}`}
              label="Cancel"
              className="iconMilestone_close"
              onClick={handleCancelClick(id)}
              color="inherit"
            />,
          ];
        }

        return [
          row?.id < 1 ? (
            <></>
          ) : (
            <GridActionsCellItem
              icon={<EditIcon />}
              key={`edit-${id}`}
              label="Edit"
              className="iconMilestone_edit"
              onClick={handleEditClick(id, row)}
              color="inherit"
            />
          ),
          row?.id < 1 ? (
            <></>
          ) : (
            <GridActionsCellItem
              key={`delete-${id}`}
              icon={<DeleteIcon />}
              sx={{ ":hover": { color: "red" } }}
              label="Delete"
              onClick={handleDeleteClick(id, row)}
              color="inherit"
              className="iconMilestone_close"
            />
          ),
        ];
      },
    },
  ];

  return (
    <>
      <div className="whiteBox">
        <div className="subscriptioncell">

          {/* ✅ Search + Add Record on the same horizontal line — both outside DataGrid */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "16px",
              flexWrap: "wrap",
              gap: "12px",
            }}
          >
            <SearchInput
              placeholder="Search Templates"
              value={searchQuery}
              onChange={setSearchQuery}
              className="searchInput"
            />
            {currentPage === 0 && (
              <Button
                className={clsx("btnTheme btnPadding", {
                  "opacity-50 cursor-not-allowed": isAddDisabled,
                })}
                onClick={handleAddClick}
                disabled={isAddDisabled}
              >
                Add template
              </Button>
            )}
          </div>

          {/* ── Horizontal scroll wrapper: works on all devices ──────────
              Instead of shrinking columns (which crushes chip content),
              the grid keeps its comfortable min width and scrolls
              sideways on narrower screens (tablet/laptop/mobile). */}
          <Box
            sx={{
              width: "100%",
              overflowX: "auto",
              WebkitOverflowScrolling: "touch",
              "&::-webkit-scrollbar": {
                height: "8px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "rgba(0,0,0,0.2)",
                borderRadius: "4px",
              },
            }}
          >
            <Box
              sx={{
                minWidth: `${GRID_MIN_WIDTH}px`,
                height: gridHeight,
                "& .actions": { color: "text.secondary" },
                "& .textPrimary": { color: "text.primary" },
                "& .new-row-border": {},
                boxShadow: "0 1px 2px rgba(0, 0, 0, .06), 0 2px 8px rgba(0, 0, 0, .05)",
              }}
            >
              <DataGrid
                rows={rows?.map((row: any, index) => ({
                  ...row,
                  key: row?._id || row.id,
                  id: row?._id || row.id,
                  templatename: row?.templateName,
                  emailContacts: row?.emailContacts,
                  emailMilestones: row?.emailMilestones,
                  waContacts: row?.waContacts,
                  waMilestones: row?.waMilestones,
                  whatsappGroups: row?.whatsappGroups,
                  whatsappGroupMilestones: row?.whatsappGroupMilestones,
                  s_no: index + 1,
                }))}
                columns={columns}
                loading={loading}
                getRowHeight={() => "auto"}
                getEstimatedRowHeight={() => 52}
                editMode="row"
                rowModesModel={rowModesModel}
                onRowModesModelChange={handleRowModesModelChange}
                onRowEditStop={handleRowEditStop}
                processRowUpdate={processRowUpdate}
                onPaginationModelChange={handlePaginationChange}
                initialState={{
                  pagination: { paginationModel: { pageSize: 10, page: 0 } },
                }}
                pageSizeOptions={[10]}
                slots={{
                  toolbar: EditToolbar as GridSlots["toolbar"],
                  loadingOverlay: CustomLoadingOverlay,
                  noRowsOverlay: CustomNoRowsOverlay,
                }}
                slotProps={{
                  loadingOverlay: { message: "Loading templates..." },
                  noRowsOverlay: { message: "No templates found" },
                }}
                sx={{
                  // Keeps the loading spinner / "No rows" message from collapsing
                  // into a tiny sliver when autoHeight has no row data to size against.
                  "--DataGrid-overlayHeight": "300px",
                  "& .MuiDataGrid-cell": {
                    display: "flex",
                    alignItems: "center",
                  },
                  "& .MuiDataGrid-row": {
                    opacity: "1 !important",
                    border: "1px solid rgba(224, 224, 224, 1)",
                    "&:hover": {
                      border: "1px solid rgba(25, 118, 210, 0.5)",
                    },
                    "&.new-row": {
                      border: "1px solid rgba(76, 175, 80, 0.5)",
                    },
                  },
                  // The grid's inner content is wider than the visible viewport
                  // (that's what makes it horizontally scrollable). Without this,
                  // the pagination footer sits at the far right of that wide
                  // content and looks like a big blank gap until you scroll over.
                  // Pinning it to the left edge keeps it visible at all times.
                  "& .MuiDataGrid-footerContainer": {
                    position: "sticky",
                    left: 0,
                    width: "100%",
                    backgroundColor: "#fff",
                    zIndex: 1,
                  },
                }}
                autoHeight={false}
                rowHeight={38}
                columnHeaderHeight={42}
              />
            </Box>
          </Box>
        </div>
      </div>
    </>
  );
}